/* eslint @typescript-eslint/strict-boolean-expressions: off */

import { useState, useEffect } from "react";
import createStore from "zustand";
import { loadStripe } from "@stripe/stripe-js";
import * as Sentry from "@sentry/react";
import {
  useTeamCheckoutSession,
  useUserCheckoutSession,
  UseCheckoutSessionOutput,
} from "src/components/page-components/SubscribeToPlanPage/hooks/useCheckoutSession";

export type CheckoutOwner =
  | { type: "user" }
  | { type: "team"; id: number; handle: string };

type RenderButtonFn = (props: {
  loading: boolean;
  onClick: () => void;
}) => React.ReactElement<HTMLButtonElement>;

interface Props {
  checkoutOwner: CheckoutOwner;
  renderButton: RenderButtonFn;
}

export function StripeSessionButton(props: Props) {
  const { checkoutOwner, renderButton } = props;

  switch (checkoutOwner.type) {
    case "team":
      return (
        <TeamStripeSessionButton
          teamId={checkoutOwner.id}
          renderButton={renderButton}
        />
      );
    case "user":
      return <UserStripeSessionButton renderButton={renderButton} />;
  }
}

function TeamStripeSessionButton(props: {
  teamId: number;
  renderButton: RenderButtonFn;
}) {
  const checkoutSession = useTeamCheckoutSession(props.teamId);

  return (
    <ComommonStripeSessionButton
      checkoutSession={checkoutSession}
      renderButton={props.renderButton}
    />
  );
}

function UserStripeSessionButton(props: { renderButton: RenderButtonFn }) {
  const checkoutSession = useUserCheckoutSession();

  return (
    <ComommonStripeSessionButton
      checkoutSession={checkoutSession}
      renderButton={props.renderButton}
    />
  );
}

function ComommonStripeSessionButton(props: {
  checkoutSession: UseCheckoutSessionOutput;
  renderButton: RenderButtonFn;
}) {
  // Set `alreadyClicked` to true if the user clicks "Add Card" before checkout is ready.
  // Will then direct to the correct next step as soon as checkout is ready.
  const [alreadyClicked, setAlreadyClicked] = useState<boolean>(false);

  const [fetchingState, refetchCheckoutSession] = props.checkoutSession;

  switch (fetchingState.status) {
    case "init":
    case "loading":
      return props.renderButton({
        loading: alreadyClicked,
        onClick: () => {
          // We don't know where to send the user yet, so just set alreadyClicked=true
          // and we will forward the user when ready.
          setAlreadyClicked(true);
        },
      });
    case "error":
      return props.renderButton({
        loading: false,
        onClick: () => {
          // Retry fetching setupSession
          refetchCheckoutSession();
          // We don't have the stripeSessionId yet, so just set alreadyClicked=true
          // and we will forward the user when ready.
          setAlreadyClicked(true);
        },
      });
    case "success":
      return (
        <GoToStripeSessionButton
          checkoutSessionId={fetchingState.checkoutSessionId}
          alreadyClicked={alreadyClicked}
          renderButton={props.renderButton}
        />
      );
  }
}

/*
 * Keep status of "goToStripe" (i.e. going to the Stripe page where
 * the user can add a card) in a store, so we can access the state in
 * a parent component, where we show error message if it fails.
 */
type Status = "init" | "loading" | "error" | "success";

const goToStripeStore = createStore<{ status: Status }>(() => ({
  status: "init",
}));

export function useGoToStripeStatus() {
  return goToStripeStore((state) => state.status);
}

function setGoToStripeStatus(status: Status) {
  goToStripeStore.setState({ status });
}

function GoToStripeSessionButton(props: {
  checkoutSessionId: string;
  alreadyClicked: boolean;
  renderButton: RenderButtonFn;
}) {
  const status = useGoToStripeStatus();

  useEffect(() => {
    // Reset the status when this component unmounts.
    return () => setGoToStripeStatus("init");
  }, []);

  function goToStripeSetup() {
    if (["loading", "success"].includes(status)) {
      return;
    }

    setGoToStripeStatus("loading");

    const sessionId = props.checkoutSessionId;

    loadStripe(stripePK)
      .then((sdk) => {
        if (!sdk) {
          throw Error("Failed to load Stripe");
        }
        return sdk.redirectToCheckout({ sessionId });
      })
      .then(
        () => {
          setGoToStripeStatus("success");
        },
        (error) => {
          setGoToStripeStatus("error");
          Sentry.captureException(error);
        }
      );
  }

  useEffect(() => {
    if (props.alreadyClicked) {
      goToStripeSetup();
    }
  }, [goToStripeSetup, props.alreadyClicked]);

  return props.renderButton({
    loading: status === "loading",
    onClick: goToStripeSetup,
  });
}

// Assert, at build time, that this env variable is present
const stripePK = (() => {
  const stripePK = process.env.NEXT_PUBLIC_STRIPE_PK;
  if (!stripePK) throw Error("NEXT_PUBLIC_STRIPE_PK not set");
  return stripePK;
})();
